import type { PipelineBoard, PipelineCard } from '@/pages/pipelines/common/types';

import {
  getInvestmentIntroductionTypeMatches,
  isPipelineNewInvestorParentCard,
} from './normalize-card';

const INTRODUCED_TIMER_WINDOWS_DAYS = {
  existingInvestor: 120,
  newInvestorParent: 90,
} as const;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const addMissingInvestmentTimers = (
  board: PipelineBoard,
  now: Date | string,
): PipelineBoard => {
  let changed = false;
  const nowMs = new Date(now).getTime();
  if (!Number.isFinite(nowMs)) return board;

  const columns = board.columns.map((column) => {
    let columnChanged = false;
    const cards = column.cards.map((card) => {
      const timer = deriveInvestmentTimer(card, nowMs);
      if (timer === card.timer) return card;

      columnChanged = true;
      changed = true;
      return { ...card, timer };
    });

    return columnChanged ? { ...column, cards } : column;
  });

  return changed ? { ...board, columns } : board;
};

export const normalizeInvestmentTimerFilterValue = (value: string) => {
  const normalized = value.toLowerCase();
  return normalized === 'amber' ? 'yellow' : normalized;
};

const deriveInvestmentTimer = (card: PipelineCard, nowMs: number): PipelineCard['timer'] => {
  if (card.timer) return normalizeTimerTone(card.timer);
  if (!card.active || !card.lastStageChangedAt) return card.timer;
  if (!hasIntroducer(card)) return card.timer;

  const introductionTypes = getInvestmentIntroductionTypeMatches(card);
  if (introductionTypes.has('None')) return card.timer;

  const windowDays =
    introductionTypes.has('NewInvestor') && isPipelineNewInvestorParentCard(card)
      ? INTRODUCED_TIMER_WINDOWS_DAYS.newInvestorParent
      : introductionTypes.has('ExistingInvestor')
        ? INTRODUCED_TIMER_WINDOWS_DAYS.existingInvestor
        : null;

  if (!windowDays) return card.timer;

  const startMs = new Date(card.lastStageChangedAt).getTime();
  if (!Number.isFinite(startMs)) return card.timer;

  const expiresAtMs = startMs + windowDays * DAY_IN_MS;
  const daysRemaining = Math.ceil((expiresAtMs - nowMs) / DAY_IN_MS);
  if (daysRemaining <= 0) return card.timer;

  return {
    label: `${daysRemaining}d`,
    tone: getTimerTone(daysRemaining),
    expiresAt: new Date(expiresAtMs).toISOString(),
  };
};

const normalizeTimerTone = (
  timer: NonNullable<PipelineCard['timer']>,
): NonNullable<PipelineCard['timer']> =>
  timer.tone === 'amber' ? { ...timer, tone: 'yellow' } : timer;

const hasIntroducer = (card: PipelineCard) =>
  Boolean(card.entityRefs.introducerUserId) ||
  card.subtitleLines.some((line) => line.trim().startsWith('via '));

const getTimerTone = (daysRemaining: number): NonNullable<PipelineCard['timer']>['tone'] => {
  if (daysRemaining < 10) return 'red';
  if (daysRemaining <= 30) return 'yellow';
  return 'green';
};
